import React, { useState, useEffect, useCallback } from 'react';
import {
    TableCell, TableRow, TableSortLabel,
    Box, LinearProgress,
    Snackbar, Alert,
    TextField,
    MenuItem as MuiMenuItem,
    Tooltip,
} from '@mui/material';
import { Assessment, Download } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { Loader } from '../../../shared/components/Loader';
import { invoiceService } from '../../../shared/services/invoiceService';
import { GstReportItem } from '../../../shared/types/invoice';

const fmtDate = (d?: string) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

const fmtAmount = (n?: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n ?? 0);

// Build financial year options from 2022-23 up to current FY
const buildFinancialYears = (): string[] => {
    const years: string[] = [];
    const now = new Date();
    const startYear = 2022;
    const endYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    for (let y = endYear; y >= startYear; y--) {
        years.push(`${y}-${String(y + 1).slice(-2)}`);
    }
    return years;
};

const FINANCIAL_YEARS = buildFinancialYears();

const fyToDateRange = (fy: string): { fromDate: string; toDate: string } => {
    const startYear = parseInt(fy.split('-')[0], 10);
    return {
        fromDate: `${startYear}-04-01`,
        toDate: `${startYear + 1}-03-31`,
    };
};

interface GrandTotals {
    grandTotalAmount: number;
    grandIgstAmount: number;
    grandCgstAmount: number;
    grandSgstAmount: number;
}

export const GstReport: React.FC = () => {
    const [items, setItems] = useState<GstReportItem[]>([]);
    const [grandTotals, setGrandTotals] = useState<GrandTotals>({ grandTotalAmount: 0, grandIgstAmount: 0, grandCgstAmount: 0, grandSgstAmount: 0 });
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });

    // Filters
    const [filterMode, setFilterMode] = useState<'fy' | 'custom'>('fy');
    const [selectedFY, setSelectedFY] = useState<string>(FINANCIAL_YEARS[0] || '');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        try {
            let params: { fromDate?: string; toDate?: string; financialYear?: string; page?: number; limit?: number } = {
                page: currentPage + 1,
                limit: pageSize,
            };
            if (filterMode === 'fy' && selectedFY) {
                params = { ...params, financialYear: selectedFY, ...fyToDateRange(selectedFY) };
            } else if (filterMode === 'custom') {
                if (fromDate) params.fromDate = fromDate;
                if (toDate) params.toDate = toDate;
            }

            const res = await invoiceService.getGstReport(params);
            const raw = res?.data;
            const list: GstReportItem[] = raw?.data || [];

            setItems(Array.isArray(list) ? list : []);
            setTotalItems(raw?.pagination?.totalItems ?? list.length);
            setGrandTotals({
                grandTotalAmount: raw?.grandTotalAmount ?? 0,
                grandIgstAmount: raw?.grandIgstAmount ?? 0,
                grandCgstAmount: raw?.grandCgstAmount ?? 0,
                grandSgstAmount: raw?.grandSgstAmount ?? 0,
            });
        } catch {
            setSnackbar({ open: true, message: 'Error loading GST report', severity: 'error' });
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [filterMode, selectedFY, fromDate, toDate, currentPage, pageSize]);

    useEffect(() => { fetchReport(); }, [fetchReport]);
    const getClientName = (inv: GstReportItem) => inv.clientCompanyName || inv.clientOwnerName || '';

    const sortedItems = React.useMemo(() => {
        if (!sortData?.active) return items;
        return [...items].sort((a, b) => {
            let av: any;
            let bv: any;
            if (sortData.active === 'clientName') {
                av = getClientName(a);
                bv = getClientName(b);
            } else {
                av = (a as any)[sortData.active] ?? '';
                bv = (b as any)[sortData.active] ?? '';
            }
            if (typeof av === 'string' && typeof bv === 'string')
                return sortData.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            if (av < bv) return sortData.direction === 'asc' ? -1 : 1;
            if (av > bv) return sortData.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [items, sortData]);

    // Grand taxable = grandTotalAmount - (grandIgst + grandCgst + grandSgst)
    const grandTaxable = grandTotals.grandTotalAmount - grandTotals.grandIgstAmount - grandTotals.grandCgstAmount - grandTotals.grandSgstAmount;
    const grandTotalTax = grandTotals.grandIgstAmount + grandTotals.grandCgstAmount + grandTotals.grandSgstAmount;

    const handleSort = (col: string) => {
        setSortData(prev =>
            prev?.active === col
                ? { active: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { active: col, direction: 'asc' }
        );
    };

    const handleExport = () => {
        const headers = [
            'Invoice Number', 'Invoice Date', 'Client', 'Client GST',
            'Taxable Amount', 'IGST', 'CGST', 'SGST', 'Total Tax', 'Total Amount',
        ];
        const rows = sortedItems.map(inv => {
            const tax = (inv.igstAmount ?? 0) + (inv.cgstAmount ?? 0) + (inv.sgstAmount ?? 0);
            const taxable = (inv.totalAmount ?? 0) - tax;
            return [
                inv.invoiceNumber,
                fmtDate(inv.invoiceDate),
                getClientName(inv),
                inv.clientGst || '',
                taxable,
                inv.igstAmount ?? 0,
                inv.cgstAmount ?? 0,
                inv.sgstAmount ?? 0,
                tax,
                inv.totalAmount ?? 0,
            ];
        });
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gst-report-${filterMode === 'fy' ? selectedFY : `${fromDate}_${toDate}`}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const SortableHeader = ({ col, label }: { col: string; label: string }) => (
        <TableCell className="!font-semibold !text-gray-700">
            <TableSortLabel
                active={sortData?.active === col}
                direction={sortData?.active === col ? sortData.direction : 'asc'}
                onClick={() => handleSort(col)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Assessment className="text-[#6366f1] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        GST Report
                    </h2>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap items-end gap-4 px-6 sm:px-10 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex gap-2">
                    <AppButton
                        variant={filterMode === 'fy' ? 'primary' : 'secondary'}
                        onClick={() => { setFilterMode('fy'); setCurrentPage(0); }}
                        sx={{ borderRadius: '8px', px: 2 }}
                    >
                        Financial Year
                    </AppButton>
                    <AppButton
                        variant={filterMode === 'custom' ? 'primary' : 'secondary'}
                        onClick={() => { setFilterMode('custom'); setCurrentPage(0); }}
                        sx={{ borderRadius: '8px', px: 2 }}
                    >
                        Custom Range
                    </AppButton>
                </div>

                {filterMode === 'fy' ? (
                    <TextField
                        select
                        label="Financial Year"
                        value={selectedFY}
                        onChange={(e) => { setSelectedFY(e.target.value); setCurrentPage(0); }}
                        size="small"
                        sx={{ minWidth: 160 }}
                    >
                        {FINANCIAL_YEARS.map(fy => (
                            <MuiMenuItem key={fy} value={fy}>FY {fy}</MuiMenuItem>
                        ))}
                    </TextField>
                ) : (
                    <>
                        <TextField
                            label="From Date"
                            type="date"
                            value={fromDate}
                            onChange={(e) => { setFromDate(e.target.value); setCurrentPage(0); }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 160 }}
                        />
                        <TextField
                            label="To Date"
                            type="date"
                            value={toDate}
                            onChange={(e) => { setToDate(e.target.value); setCurrentPage(0); }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 160 }}
                        />
                    </>
                )}
            </div>

            {/* Action Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 sm:px-10 py-2 border-b border-gray-100">
                <Tooltip title="Export GST Report">
                    <AppButton
                        variant="primary"
                        onClick={handleExport}
                        sx={{ borderRadius: '8px', backgroundColor: '#0EA5E9', '&:hover': { backgroundColor: '#0284C7' }, px: 2, gap: 1 }}
                    >
                        <Download fontSize="small" />
                        Export CSV
                    </AppButton>
                </Tooltip>
                <div className="flex gap-2 flex-wrap">
                    <AppButton
                        variant="primary"
                        disableRipple
                        sx={{ backgroundColor: '#f59e0b', '&:hover': { backgroundColor: '#f59e0b' }, cursor: 'default', px: 2, py: 1, borderRadius: '6px' }}
                    >
                        Total Tax: {fmtAmount(grandTotalTax)}
                    </AppButton>
                    <AppButton
                        variant="primary"
                        disableRipple
                        sx={{ backgroundColor: '#22c55e', '&:hover': { backgroundColor: '#22c55e' }, cursor: 'default', px: 2, py: 1, borderRadius: '6px' }}
                    >
                        Total Amount: {fmtAmount(grandTotals.grandTotalAmount)}
                    </AppButton>
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-x-auto">
                <div className="flex flex-col w-full flex-1">
                    {isLoading && items.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && items.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <SortableHeader col="invoiceNumber" label="Invoice No." />
                                    <SortableHeader col="invoiceDate" label="Invoice Date" />
                                    <SortableHeader col="clientName" label="Client" />
                                    <TableCell className="!font-semibold !text-gray-700">Client GST</TableCell>
                                    <SortableHeader col="totalAmount" label="Taxable Amt" />
                                    <SortableHeader col="igstAmount" label="IGST" />
                                    <SortableHeader col="cgstAmount" label="CGST" />
                                    <SortableHeader col="sgstAmount" label="SGST" />
                                    <TableCell className="!font-semibold !text-gray-700">Total Tax</TableCell>
                                    <SortableHeader col="totalAmount" label="Total Amt" />
                                </TableRow>
                            }
                            isEmpty={sortedItems.length === 0}
                            emptyColSpan={11}
                        >
                            {sortedItems.map((inv, i) => {
                                const tax = (inv.igstAmount ?? 0) + (inv.cgstAmount ?? 0) + (inv.sgstAmount ?? 0);
                                const taxable = (inv.totalAmount ?? 0) - tax;
                                return (
                                    <TableRow key={inv._id ?? i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <TableCell className="!text-gray-500 !text-sm">
                                            {currentPage * pageSize + i + 1}
                                        </TableCell>
                                        <TableCell className="!text-gray-900 !font-medium">
                                            {inv.invoiceNumber}
                                        </TableCell>
                                        <TableCell className="!text-gray-600">{fmtDate(inv.invoiceDate)}</TableCell>
                                        <TableCell className="!text-gray-700">{getClientName(inv) || '-'}</TableCell>
                                        <TableCell className="!text-gray-500 !text-sm font-mono">
                                            {inv.clientGst || '—'}
                                        </TableCell>
                                        <TableCell className="!text-gray-700">{fmtAmount(taxable)}</TableCell>
                                        <TableCell className="!text-gray-700">{fmtAmount(inv.igstAmount)}</TableCell>
                                        <TableCell className="!text-gray-700">{fmtAmount(inv.cgstAmount)}</TableCell>
                                        <TableCell className="!text-gray-700">{fmtAmount(inv.sgstAmount)}</TableCell>
                                        <TableCell className="!text-gray-700 !font-semibold">{fmtAmount(tax)}</TableCell>
                                        <TableCell className="!text-gray-700 !font-semibold">{fmtAmount(inv.totalAmount)}</TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* Grand Totals row — from API */}
                            {totalItems > 0 && (
                                <TableRow className="bg-blue-50 border-t-2 border-blue-200">
                                    <TableCell colSpan={5} className="!font-bold !text-gray-800">
                                        GRAND TOTAL ({totalItems} invoices)
                                    </TableCell>
                                    <TableCell className="!font-bold !text-gray-900">{fmtAmount(grandTaxable)}</TableCell>
                                    <TableCell className="!font-bold !text-gray-900">{fmtAmount(grandTotals.grandIgstAmount)}</TableCell>
                                    <TableCell className="!font-bold !text-gray-900">{fmtAmount(grandTotals.grandCgstAmount)}</TableCell>
                                    <TableCell className="!font-bold !text-gray-900">{fmtAmount(grandTotals.grandSgstAmount)}</TableCell>
                                    <TableCell className="!font-bold !text-blue-700">{fmtAmount(grandTotalTax)}</TableCell>
                                    <TableCell className="!font-bold !text-blue-700">{fmtAmount(grandTotals.grandTotalAmount)}</TableCell>
                                </TableRow>
                            )}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={totalItems}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setCurrentPage(p - 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
                />
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

